import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../../../Components/Request';

export interface UserRow {
  id: string;
  name: string;
  email: string;
  status_id: number;
  loadCount?: number;
}

export const useUsers = () => {
  const navigate = useNavigate();
  const navRef = useRef(navigate);
  useEffect(() => {
    navRef.current = navigate;
  }, [navigate]);

  const [users, setUsers] = useState<UserRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');

  // debounce del buscador
  useEffect(() => {
    const t = window.setTimeout(() => setDebounced(search.trim()), 350);
    return () => window.clearTimeout(t);
  }, [search]);

  const fetchUsers = useCallback(async (q: string) => {
    setLoading(true);
    try {
      const res = await axiosInstance.get('/users', { params: { search: q || undefined } });
      setUsers(res.data || []);
    } catch (err: any) {
      const status = err?.response?.status;
      if (status === 401) navRef.current('/login');
      else if (status === 403) navRef.current('/');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers(debounced);
  }, [debounced, fetchUsers]);

  const counts = {
    total: users.length,
    active: users.filter((u) => u.status_id === 1).length,
    inactive: users.filter((u) => u.status_id !== 1).length,
  };

  const filteredUsers = users.filter((u) => {
    if (statusFilter === 'active') return u.status_id === 1;
    if (statusFilter === 'inactive') return u.status_id !== 1;
    return true;
  });

  /**
   * Paginación **en el navegador**, y aquí sí es la opción correcta.
   *
   * 🔑 Son 31 usuarios medidos en la base, así que el problema que reportó el usuario no es la
   * transferencia —bajar 31 filas no cuesta nada— sino el **scroll largo** de la tabla. Eso lo
   * resuelve cortar la vista, no cambiar el endpoint.
   *
   * ⚠️ Y pedirlo al servidor **rompería dos cosas** por ningún beneficio: los contadores de
   * "Total / Active / Inactive" se calculan del conjunto completo, y el filtro por estatus también.
   * Con una página de 20, ambos pasarían a describir solo esa página — el mismo error que hubo que
   * corregir en Loads, donde sí valía la pena porque son 2,149 filas.
   *
   * 📌 El día que esta tabla crezca de verdad, el patrón para moverla ya está hecho en `useLoadsData`
   * y el backend tiene `paginatedListService`.
   */
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const totalPages = Math.max(1, Math.ceil(filteredUsers.length / pageSize));
  const currentPage = Math.min(page, totalPages);

  // Volver a la primera al cambiar de filtro o de búsqueda: quedarse en la página 3 de un
  // resultado que ahora tiene una mostraría una tabla vacía.
  useEffect(() => {
    setPage(1);
  }, [debounced, statusFilter, pageSize]);

  const pageStart = (currentPage - 1) * pageSize;
  const visibleUsers = filteredUsers.slice(pageStart, pageStart + pageSize);

  return {
    users: visibleUsers,
    /** Total tras filtros, para el rango del paginador. Distinto de `counts.total`, que es el global. */
    filteredTotal: filteredUsers.length,
    loading,
    search,
    setSearch,
    statusFilter,
    setStatusFilter,
    counts,
    page: currentPage,
    setPage,
    pageSize,
    setPageSize,
    totalPages,
    openDetail: (id: string) => navRef.current(`/users/${id}`),
  };
};
